import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Cropper from 'react-easy-crop';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImageBlob = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
  });
};

const Wrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Heading = styled.h2`
  font-size: 24px;
  margin-bottom: 8px;
  font-weight: 700;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
`;

const Card = styled.div`
  display: flex;
  gap: 24px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
`;

const AvatarImage = styled.img`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 999px;
  object-fit: cover;
  border: 2px solid #e5e7eb;
`;

const AvatarBlock = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const AvatarPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 999px;
  border: 2px dashed #d1d5db;
  background: #f9fafb;
  color: #9ca3af;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4px;
`;

const AvatarUploadLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #4f46e5;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  input {
    display: none;
  }
`;

const AvatarPendingNote = styled.div`
  font-size: 10px;
  text-align: center;
  color: #92400e;
`;

const AvatarCancelLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 10px;
  color: #b91c1c;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.6);
  padding: 16px;
`;

const ModalPanel = styled.div`
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.3);
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const CropperContainer = styled.div`
  position: relative;
  width: 100%;
  height: 320px;
  background: #111827;
  border-radius: 8px;
  overflow: hidden;
`;

const ZoomRow = styled.div`
  margin-top: 16px;
`;

const ZoomLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
`;

const ZoomSlider = styled.input`
  width: 100%;
  cursor: pointer;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e5e7eb;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
`;

const Username = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
`;

const Grid = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px 16px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin-bottom: 2px;
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: #374151;
`;

const EmptyValue = styled.span`
  color: #9ca3af;
  font-style: italic;
`;

const VerificationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 11px;
  font-weight: 600;
`;

const EditButton = styled.button`
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
`;

const FormGrid = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.full {
    grid-column: 1 / -1;
  }
`;

const inputStyle = `
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 14px;
  color: #111827;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }
`;

const Input = styled.input`
  ${inputStyle}
`;

const Select = styled.select`
  ${inputStyle}
`;

const TextArea = styled.textarea`
  ${inputStyle}
  min-height: 70px;
  resize: vertical;
`;

const FormActions = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 10px;
`;

const PrimaryButton = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  background: #4f46e5;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #4338ca;
  }

  &:disabled {
    background: #a5b4fc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
`;

const SectionCard = styled.div`
  margin-top: 20px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
`;

const SectionTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const SectionSubtitle = styled.p`
  margin: 0 0 14px;
  font-size: 13px;
  color: #6b7280;
`;

const MobileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const MobileItemMain = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const MobileNumberText = styled.span`
  font-size: 14px;
  color: #111827;
  font-weight: 500;
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  font-size: 11px;
  font-weight: 600;
`;

const PendingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 11px;
  font-weight: 600;
`;

const RemoveButton = styled.button`
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #fee2e2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AddMobileRow = styled.form`
  margin-top: 12px;
  display: flex;
  gap: 8px;
`;

const AddMobileInput = styled(Input)`
  flex: 1;
`;

const Banner = styled.div`
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;

  &.error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
  }

  &.success {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #065f46;
  }
`;

const FIELD_META = [
  { key: 'fullName', label: 'Full name', type: 'text', full: true },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'telephone', label: 'Telephone', type: 'text' },
  { key: 'dob', label: 'Date of birth', type: 'date' },
  { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'] },
  { key: 'nicNumber', label: 'NIC number', type: 'text' },
  { key: 'address', label: 'Address', type: 'textarea', full: true },
];

export default function Profile() {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingByField, setPendingByField] = useState({});
  const [editing, setEditing] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [mobileNumbers, setMobileNumbers] = useState([]);
  const [newMobileNumber, setNewMobileNumber] = useState('');
  const [addingMobile, setAddingMobile] = useState(false);
  const [removingMobileId, setRemovingMobileId] = useState(null);
  const [mobileError, setMobileError] = useState(null);
  const [pendingImageRequest, setPendingImageRequest] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cancellingImage, setCancellingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropSelectedFile, setCropSelectedFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const loadProfile = () => {
    return axiosInstance.get('/students/me').then((resp) => {
      const data = resp?.data || {};
      setStudent(data.student || null);
    });
  };

  const loadPendingRequests = () => {
    return axiosInstance
      .get('/students/me/profile-edit-requests')
      .then((resp) => {
        const requests = resp?.data?.requests || [];
        const map = {};
        requests.forEach((r) => {
          map[r.fieldName] = r;
        });
        setPendingByField(map);
      })
      .catch((err) => {
        console.error('Failed to fetch pending profile edit requests', err);
      });
  };

  const loadMobileNumbers = () => {
    return axiosInstance
      .get('/students/me/mobile-numbers')
      .then((resp) => {
        setMobileNumbers(resp?.data?.mobileNumbers || []);
      })
      .catch((err) => {
        console.error('Failed to fetch mobile numbers', err);
      });
  };

  const loadPendingImageRequest = () => {
    return axiosInstance
      .get('/students/me/image-edit-requests')
      .then((resp) => {
        setPendingImageRequest(resp?.data?.request || null);
      })
      .catch((err) => {
        console.error('Failed to fetch pending image request', err);
      });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadProfile(), loadPendingRequests(), loadMobileNumbers(), loadPendingImageRequest()])
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch student profile', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const toDateInputValue = (value) => {
    if (!value) return '';
    return String(value).slice(0, 10);
  };

  const renderValue = (value) => {
    if (!value) {
      return <EmptyValue>Not provided</EmptyValue>;
    }
    return <FieldValue>{value}</FieldValue>;
  };

  const startEditing = () => {
    setFormValues({
      fullName: student?.fullName || '',
      email: student?.email || '',
      telephone: student?.telephone || '',
      dob: toDateInputValue(student?.dob),
      gender: student?.gender || '',
      nicNumber: student?.nicNumber || '',
      address: student?.address || '',
    });
    setFormError(null);
    setFormSuccess(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setFormError(null);
  };

  const handleFieldChange = (key) => (event) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const changedFields = useMemo(() => {
    if (!student || !editing) return {};
    const changes = {};
    FIELD_META.forEach(({ key }) => {
      const current = key === 'dob' ? toDateInputValue(student.dob) : student[key] || '';
      const next = formValues[key] ?? '';
      if (next !== current) {
        changes[key] = next;
      }
    });
    return changes;
  }, [student, formValues, editing]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (Object.keys(changedFields).length === 0) {
      setFormError('No changes to submit.');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await axiosInstance.post('/students/me/profile-edit-requests', changedFields);
      setFormSuccess(resp?.data?.message || 'Your changes were submitted for admin approval.');
      setEditing(false);
      await loadPendingRequests();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to submit your changes. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMobileNumber = async (event) => {
    event.preventDefault();
    setMobileError(null);

    const trimmed = newMobileNumber.trim();
    if (!trimmed) {
      setMobileError('Please enter a mobile number.');
      return;
    }

    setAddingMobile(true);
    try {
      const resp = await axiosInstance.post('/students/me/mobile-numbers', { mobileNumber: trimmed });
      const added = resp?.data?.mobileNumber;
      if (added) {
        setMobileNumbers((prev) => [added, ...prev]);
      } else {
        await loadMobileNumbers();
      }
      setNewMobileNumber('');
    } catch (err) {
      setMobileError(err?.response?.data?.message || 'Failed to add mobile number. Please try again.');
    } finally {
      setAddingMobile(false);
    }
  };

  const handleRemoveMobileNumber = async (requestId) => {
    if (!window.confirm('Remove this mobile number?')) {
      return;
    }
    setMobileError(null);
    setRemovingMobileId(requestId);
    try {
      await axiosInstance.delete(`/students/me/mobile-numbers/${requestId}`);
      setMobileNumbers((prev) => prev.filter((m) => m.requestId !== requestId));
    } catch (err) {
      setMobileError(err?.response?.data?.message || 'Failed to remove mobile number. Please try again.');
    } finally {
      setRemovingMobileId(null);
    }
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setImageError('File size must be less than 10MB.');
      return;
    }

    setImageError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setCropSelectedFile(file);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const closeCropModal = () => {
    setCropModalOpen(false);
    setCropImageSrc(null);
    setCropSelectedFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleConfirmCrop = async () => {
    if (!cropImageSrc || !croppedAreaPixels) return;

    setImageError(null);
    setUploadingImage(true);
    try {
      const croppedBlob = await getCroppedImageBlob(cropImageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append('image', croppedBlob, cropSelectedFile?.name || 'profile.jpg');
      const resp = await axiosInstance.post('/students/me/image-edit-requests', formData);
      setPendingImageRequest(resp?.data?.request || null);
      closeCropModal();
    } catch (err) {
      setImageError(err?.response?.data?.message || 'Failed to upload photo. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCancelImageRequest = async () => {
    if (!pendingImageRequest || !window.confirm('Cancel this pending photo request?')) {
      return;
    }
    setImageError(null);
    setCancellingImage(true);
    try {
      await axiosInstance.delete(`/students/me/image-edit-requests/${pendingImageRequest.requestId}`);
      setPendingImageRequest(null);
    } catch (err) {
      setImageError(err?.response?.data?.message || 'Failed to cancel photo request. Please try again.');
    } finally {
      setCancellingImage(false);
    }
  };

  return (
    <StudentLayout title="Profile">
      <Wrapper>
        <HeaderRow>
          <div>
            <Heading>Your Profile</Heading>
            <Subtitle>View the personal details associated with your student account.</Subtitle>
          </div>
          {!loading && student && !editing && (
            <EditButton type="button" onClick={startEditing}>
              Edit details
            </EditButton>
          )}
        </HeaderRow>

        {loading && <Loader message="Loading your profile…" />}

        {!loading && student && (
          <Card>
            <AvatarBlock>
              {pendingImageRequest ? (
                <AvatarImage
                  src={getImageURL(pendingImageRequest.imagePath)}
                  alt="Pending profile photo"
                  style={{ opacity: 0.6 }}
                />
              ) : student.imagePath ? (
                <AvatarImage
                  src={getImageURL(student.imagePath)}
                  alt={student.fullName || 'Student profile'}
                />
              ) : (
                <AvatarPlaceholder>No photo</AvatarPlaceholder>
              )}

              {pendingImageRequest ? (
                <>
                  <AvatarPendingNote>⏳ Waiting for approval</AvatarPendingNote>
                  <AvatarCancelLink type="button" onClick={handleCancelImageRequest} disabled={cancellingImage}>
                    {cancellingImage ? 'Cancelling…' : 'Cancel'}
                  </AvatarCancelLink>
                </>
              ) : (
                <AvatarUploadLabel>
                  {uploadingImage ? 'Uploading…' : 'Upload photo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    onChange={handleImageFileChange}
                    disabled={uploadingImage}
                  />
                </AvatarUploadLabel>
              )}
              {imageError && (
                <AvatarPendingNote style={{ color: '#b91c1c', maxWidth: 90 }}>{imageError}</AvatarPendingNote>
              )}
            </AvatarBlock>
            <Info>
              <Name>{student.fullName || 'Student'}</Name>
              <Username>Username: {student.username || '—'}</Username>

              {!editing ? (
                <Grid>
                  {FIELD_META.map(({ key, label, full }) => {
                    const pending = pendingByField[key];
                    const displayValue = key === 'dob' ? formatDate(student.dob) : student[key];
                    return (
                      <div key={key} style={full ? { gridColumn: '1 / -1' } : undefined}>
                        <FieldLabel>{label}</FieldLabel>
                        {renderValue(displayValue)}
                        {pending && (
                          <VerificationBadge title={`Requested change: ${pending.newValue || '—'}`}>
                            ⏳ Waiting for verification
                          </VerificationBadge>
                        )}
                      </div>
                    );
                  })}
                </Grid>
              ) : (
                <form onSubmit={handleSubmit}>
                  <FormGrid>
                    {FIELD_META.map(({ key, label, type, full, options }) => (
                      <Field key={key} className={full ? 'full' : undefined}>
                        <FieldLabel>{label}</FieldLabel>
                        {type === 'select' ? (
                          <Select value={formValues[key] || ''} onChange={handleFieldChange(key)}>
                            <option value="">Not provided</option>
                            {options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </Select>
                        ) : type === 'textarea' ? (
                          <TextArea value={formValues[key] || ''} onChange={handleFieldChange(key)} />
                        ) : (
                          <Input type={type} value={formValues[key] || ''} onChange={handleFieldChange(key)} />
                        )}
                        {pendingByField[key] && (
                          <VerificationBadge>⏳ Waiting for verification</VerificationBadge>
                        )}
                      </Field>
                    ))}
                  </FormGrid>

                  {formError && <Banner className="error">{formError}</Banner>}

                  <FormActions>
                    <PrimaryButton type="submit" disabled={submitting}>
                      {submitting ? 'Submitting…' : 'Submit for approval'}
                    </PrimaryButton>
                    <SecondaryButton type="button" onClick={cancelEditing} disabled={submitting}>
                      Cancel
                    </SecondaryButton>
                  </FormActions>
                </form>
              )}

              {!editing && formSuccess && <Banner className="success">{formSuccess}</Banner>}
            </Info>
          </Card>
        )}

        {!loading && student && (
          <SectionCard>
            <SectionTitle>Mobile Numbers</SectionTitle>
            <SectionSubtitle>
              Add extra mobile numbers for contact purposes. New numbers wait for admin approval before they're
              marked as verified.
            </SectionSubtitle>

            {mobileNumbers.length > 0 && (
              <MobileList>
                {mobileNumbers.map((m) => (
                  <MobileItem key={m.requestId}>
                    <MobileItemMain>
                      <MobileNumberText>{m.mobileNumber}</MobileNumberText>
                      {m.status === 'approved' ? (
                        <VerifiedBadge>✓ Verified</VerifiedBadge>
                      ) : (
                        <PendingBadge>⏳ Waiting for verification</PendingBadge>
                      )}
                    </MobileItemMain>
                    <RemoveButton
                      type="button"
                      onClick={() => handleRemoveMobileNumber(m.requestId)}
                      disabled={removingMobileId === m.requestId}
                    >
                      {removingMobileId === m.requestId ? 'Removing…' : 'Remove'}
                    </RemoveButton>
                  </MobileItem>
                ))}
              </MobileList>
            )}

            <AddMobileRow onSubmit={handleAddMobileNumber}>
              <AddMobileInput
                type="text"
                placeholder="Add another mobile number"
                value={newMobileNumber}
                onChange={(event) => setNewMobileNumber(event.target.value)}
              />
              <PrimaryButton type="submit" disabled={addingMobile}>
                {addingMobile ? 'Adding…' : 'Add'}
              </PrimaryButton>
            </AddMobileRow>

            {mobileError && <Banner className="error">{mobileError}</Banner>}
          </SectionCard>
        )}
      </Wrapper>

      {cropModalOpen && cropImageSrc && (
        <ModalOverlay>
          <ModalPanel>
            <ModalHeader>Adjust your photo</ModalHeader>
            <ModalBody>
              <CropperContainer>
                <Cropper
                  image={cropImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </CropperContainer>
              <ZoomRow>
                <ZoomLabel>Zoom</ZoomLabel>
                <ZoomSlider
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                />
              </ZoomRow>
              {imageError && <Banner className="error">{imageError}</Banner>}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton type="button" onClick={closeCropModal} disabled={uploadingImage}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={handleConfirmCrop} disabled={uploadingImage || !croppedAreaPixels}>
                {uploadingImage ? 'Uploading…' : 'Submit for approval'}
              </PrimaryButton>
            </ModalFooter>
          </ModalPanel>
        </ModalOverlay>
      )}
    </StudentLayout>
  );
}
